import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { useSelector } from "react-redux";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { AuthContext } from "../../../Header/auth - context";
import useVisinaTabele from "../../../custom/visinaTabele";
import "../Fakture.css";

const iznos = (v) =>
    v === null || v === undefined || v === ""
        ? "0,00"
        : Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* Prihvaćena faktura je obaveza koja postoji na SEF-u, a dok se ne unese u TMS
   ne postoji ni u knjigama ni u obavezama. Ovaj spisak je tačno ta rupa: šta je
   prihvaćeno a nije knjiženo. Nestaje sam kad se faktura unese — nema zasebnog
   statusa koji neko mora da održava. */
const FaktureZaUnos = ({ naUnos }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((s) => s.company);
    const toast = useRef(null);

    const [podaci, setPodaci] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [izabrani, setIzabrani] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [potvrda, setPotvrda] = useState(null);
    /* { dok, kandidati: [...], izabrana: id|null } — otvara se samo kad se nađe
       već uneta faktura istog dobavljača. */
    const [duplikat, setDuplikat] = useState(null);
    const [proverava, setProverava] = useState(false);
    /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
    const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([podaci, izabrani], 180);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 5000 });

    const posalji = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/sef/" + putanja, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || t("common.greska"));
        return d;
    };

    const greska = (e) => poruka("error", t("common.greska"), e.message);

    const ucitaj = () => {
        setUcitava(true);
        posalji("UlazneZaUnos")
            .then((d) => { setPodaci(d || []); setIzabrani(null); })
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    /* Provera ide pre svega ostalog: ako je faktura već uneta, pitanje „uneti?"
       je pogrešno pitanje. */
    /* KO/KZ sa SEF-a nije faktura: provera "već uneta faktura" za njega nema smisla
       (traži fakture), pa ide pravo na potvrdu i zatim u ekran KO / KZ. */
    const jeKoKz = (dok) => /credit|debit|381|383/i.test(String(dok?.DocumentType || ""));
    const tipNaziv = (dok) => /debit|383/i.test(String(dok?.DocumentType || "")) ? t("ukokz.kz")
        : /credit|381/i.test(String(dok?.DocumentType || "")) ? t("ukokz.ko") : t("ukokz.faktura");

    const zapocni = (dok) => {
        if (!dok) return;
        if (jeKoKz(dok)) { setPotvrda(dok); return; }
        setProverava(true);
        posalji("UlazniPostojeca", { id: dok.id })
            .then((d) => {
                const k = d.kandidati || [];
                if (k.length) setDuplikat({ dok, kandidati: k, izabrana: k[0]?.id_faktura ?? null });
                else setPotvrda(dok);
            })
            .catch((e) => { greska(e); setPotvrda(dok); })   // provera ne sme da blokira rad
            .finally(() => setProverava(false));
    };

    /* Vezivanje za postojeću fakturu — dokument nestaje iz spiska, a ništa se
       ne knjiži dvaput. */
    const poveziSaPostojecom = () => {
        if (!duplikat?.izabrana) return;
        setProverava(true);
        posalji("UlazniVeziFakturu", { id: duplikat.dok.id, id_faktura: duplikat.izabrana })
            .then((d) => {
                poruka("success", t("sefUlazne.povezano"), d.message || t("sefUlazne.povezanoOpis"));
                setDuplikat(null);
                ucitaj();
            })
            .catch(greska)
            .finally(() => setProverava(false));
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Za unos");
        const headers = [
            t("sefUlazne.broj"), t("ulazne.dobavljac"), t("sefUlazne.pib"),
            t("ulazne.neto"), t("ulazne.pdv"), t("ulazne.bruto"), t("ulazne.valuta"),
            t("sefUlazne.poslato"), t("ulazne.rokPlacanja"), t("sefUlazne.odluka"),
            t("sefUlazne.danaOdOdluke"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (podaci || []).forEach((r) => {
            const row = worksheet.addRow([
                r.DocumentNumber || "", r.SupplierName || "", r.SupplierVat || "",
                Number(r.SumWithoutVat || 0), Number(r.VatAmount || 0), Number(r.Amount || 0),
                r.Currency || "", r.SentDateTekst || "", r.DueDateTekst || "",
                r.DatumOdluke || "", r.DanaOdOdluke ?? "",
            ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${t("sefUlazne.zaUnos")} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcel} data-pr-tooltip="XLS" />
                <Button type="button" icon="pi pi-refresh" rounded outlined
                    onClick={ucitaj} data-pr-tooltip={t("sefUlazne.ucitaj")} />
            </div>
            <input type="search" placeholder={t("nova.phPretraga")} className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="faktureStranica">
                <Card>
                    <div className="sef-ulazne-napomena">
                        <i className="pi pi-info-circle" /> {t("sefUlazne.zaUnosOpis")}
                    </div>

                    <div ref={omotTabele}>
                    <DataTable
                        value={podaci || []}
                        paginator rows={redovaTabele} rowsPerPageOptions={[redovaTabele, 50, 100]}
                        globalFilter={globalFilter}
                        header={tableHeader}
                        loading={ucitava}
                        scrollable scrollHeight={visinaTabele}
                        emptyMessage={t("sefUlazne.nemaZaUnos")}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        selectionMode="single" selection={izabrani}
                        onSelectionChange={(e) => setIzabrani(e.value)}
                        onRowDoubleClick={(e) => zapocni(e.data)}
                        /* Što duže stoji neknjižena, to je hitnija — posle 7 dana crveno. */
                        rowClassName={(r) => ({ "sef-red-istekao": Number(r.DanaOdOdluke) >= 7 })}>
                        <Column header={t("nova.tip")} style={{ minWidth: "90px" }}
                            body={(r) => <span className={`sef-pilula ${jeKoKz(r) ? "sef-st-upozorenje" : "sef-st-info"}`}>{tipNaziv(r)}</span>} />
                        <Column field="DocumentNumber" header={t("sefUlazne.broj")} style={{ minWidth: "140px" }} />
                        <Column field="SupplierName" header={t("ulazne.dobavljac")} style={{ minWidth: "200px" }} />
                        <Column field="SupplierVat" header={t("sefUlazne.pib")} style={{ minWidth: "110px" }} />
                        <Column header={t("ulazne.neto")} style={{ minWidth: "120px" }}
                            body={(r) => <span className="sef-iznos">{iznos(r.SumWithoutVat)}</span>} />
                        <Column header={t("ulazne.pdv")} style={{ minWidth: "110px" }}
                            body={(r) => <span className="sef-iznos">{iznos(r.VatAmount)}</span>} />
                        <Column header={t("ulazne.bruto")} style={{ minWidth: "130px" }}
                            body={(r) => <strong className="sef-iznos">{iznos(r.Amount)}</strong>} />
                        <Column field="Currency" header={t("ulazne.valuta")} style={{ minWidth: "90px" }} />
                        <Column field="SentDateTekst" header={t("sefUlazne.poslato")} style={{ minWidth: "110px" }} />
                        <Column field="DueDateTekst" header={t("ulazne.rokPlacanja")} style={{ minWidth: "110px" }} />
                        <Column field="ProverioIme" header={t("sefUlazne.proverio")} style={{ minWidth: "160px" }}
                            body={(r) => r.ProverioIme || r.OdlucioKorisnik || "—"} />
                        <Column header={t("sefUlazne.danaOdOdluke")} style={{ minWidth: "120px" }}
                            body={(r) => (
                                <span className={`sef-pilula ${Number(r.DanaOdOdluke) >= 7 ? "sef-st-kritican" : "sef-st-ok"}`}>
                                    {r.DanaOdOdluke ?? 0}
                                </span>
                            )} />
                    </DataTable>
                    </div>

                    <div className="sef-ulazne-akcije">
                        <span className="sef-izabrano">
                            {izabrani
                                ? `${izabrani.DocumentNumber} · ${izabrani.SupplierName}`
                                : t("sefUlazne.izaberiRed")}
                        </span>
                        <Button type="button" label={t("sefUlazne.unesi")} icon="pi pi-file-import"
                            className="p-button-sm" severity="success" loading={proverava}
                            disabled={!izabrani} onClick={() => zapocni(izabrani)} />
                    </div>
                </Card>
            </div>

            {/* ─────────── faktura je možda već uneta ─────────── */}
            <Dialog header={t("sefUlazne.duplikatNaslov")} visible={!!duplikat} style={{ width: "50rem" }}
                breakpoints={{ "768px": "95vw" }} onHide={() => setDuplikat(null)}>
                {duplikat && (
                    <div className="sef-potvrda">
                        <p className="sef-potvrda-upozorenje">
                            <i className="pi pi-exclamation-triangle" />{" "}
                            {t("sefUlazne.duplikatOpis", {
                                broj: duplikat.dok.DocumentNumber,
                                dobavljac: duplikat.dok.SupplierName,
                                iznos: `${iznos(duplikat.dok.Amount)} ${duplikat.dok.Currency || ""}`,
                            })}
                        </p>

                        <DataTable value={duplikat.kandidati} size="small" showGridlines
                            className="turePregledTable stavke-tabela"
                            selectionMode="single"
                            selection={duplikat.kandidati.find((k) => k.id_faktura === duplikat.izabrana) || null}
                            onSelectionChange={(e) => setDuplikat((p) => ({ ...p, izabrana: e.value?.id_faktura ?? null }))}>
                            <Column field="Broj" header={t("nova.fakturaBr")} style={{ minWidth: "110px" }} />
                            <Column field="refNo" header={t("izvod.brojFakture")} style={{ minWidth: "140px" }} />
                            <Column field="Klijent" header={t("ulazne.dobavljac")} style={{ minWidth: "190px" }} />
                            <Column field="Datum" header={t("izvod.datum")} style={{ minWidth: "110px" }} />
                            <Column header={t("ulazne.bruto")} style={{ minWidth: "120px" }}
                                body={(r) => <span className="sef-iznos">{iznos(r.Iznos)}</span>} />
                            <Column header={t("sefUlazne.poklapanje")} style={{ minWidth: "130px" }}
                                body={(r) => (
                                    <span className={`sef-pilula ${r.Poklapanje === "OBA" ? "sef-st-kritican" : "sef-st-upozorenje"}`}>
                                        {t("sefUlazne.poklapanje" + r.Poklapanje)}
                                    </span>
                                )} />
                        </DataTable>

                        <div className="sef-ulazne-napomena" style={{ marginTop: "0.6rem" }}>
                            <i className="pi pi-info-circle" /> {t("sefUlazne.duplikatUputstvo")}
                        </div>

                        <div className="sef-potvrda-dugmad">
                            <Button type="button" label={t("common.cancel")} className="p-button-text"
                                onClick={() => setDuplikat(null)} />
                            <Button type="button" label={t("sefUlazne.unesiIpak")} icon="pi pi-file-import"
                                className="p-button-outlined"
                                onClick={() => { const d = duplikat.dok; setDuplikat(null); setPotvrda(d); }} />
                            <Button type="button" label={t("sefUlazne.poveziSaPostojecom")} icon="pi pi-link"
                                severity="success" loading={proverava} disabled={!duplikat.izabrana}
                                onClick={poveziSaPostojecom} />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Potvrda pre prelaska na formu — prelazak menja ekran, pa se ne radi
                slučajnim dvoklikom bez pitanja. */}
            <Dialog header={t("sefUlazne.unesiNaslov")} visible={!!potvrda} style={{ width: "36rem" }}
                breakpoints={{ "768px": "95vw" }} onHide={() => setPotvrda(null)}>
                {potvrda && (
                    <div className="sef-potvrda">
                        <p className="sef-potvrda-pitanje">
                            {t("sefUlazne.unesiPitanje", {
                                broj: potvrda.DocumentNumber,
                                dobavljac: potvrda.SupplierName,
                                iznos: `${iznos(potvrda.Amount)} ${potvrda.Currency || ""}`,
                            })}
                        </p>
                        <div className="sef-ulazne-napomena" style={{ marginTop: 0 }}>
                            <i className="pi pi-info-circle" /> {t("sefUlazne.unesiOpis")}
                        </div>
                        <div className="sef-potvrda-dugmad">
                            <Button type="button" label={t("common.cancel")} className="p-button-text"
                                onClick={() => setPotvrda(null)} />
                            <Button type="button" label={t("sefUlazne.unesi")} icon="pi pi-file-import"
                                severity="success"
                                onClick={() => { const d = potvrda; setPotvrda(null); naUnos?.(d); }} />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default FaktureZaUnos;
